import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import PropertyCard from "@/components/PropertyCard";
import LeadForm from "@/components/LeadForm";
import GatedLeadModal from "@/components/GatedLeadModal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  MapPin, Building2, Calendar, ShieldCheck, MessageCircle, Phone, Download,
  CheckCircle2, Home as HomeIcon, School, Hospital, Briefcase, Train, ChevronLeft, ChevronRight, Layers
} from "lucide-react";

export default function PropertyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings, waLink, telLink } = useSettings();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [gate, setGate] = useState(null); // {leadType,title,onUnlock}

  useEffect(() => {
    setP(null); setNotFound(false); setActive(0);
    window.scrollTo(0, 0);
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center">
      <h1 className="font-display text-3xl text-navy">Project Not Found</h1>
      <p className="text-muted-foreground mt-3">This project may have been removed or the link is incorrect.</p>
      <Button onClick={() => navigate("/properties")} className="mt-6 bg-gold text-navy hover:bg-gold-dark">Browse Properties</Button>
    </div>
  );

  if (!p) return (
    <div className="max-w-7xl mx-auto px-4 py-10 space-y-6">
      <Skeleton className="h-[420px] rounded-2xl" />
      <div className="grid lg:grid-cols-3 gap-8"><div className="lg:col-span-2 space-y-4"><Skeleton className="h-40" /><Skeleton className="h-64" /></div><Skeleton className="h-80" /></div>
    </div>
  );

  const waMsg = `Hi, I'm interested in ${p.name} by ${p.builder} at ${p.location}. Please share details.`;
  const images = p.images || [];
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(p.map_query || p.location + ", Bangalore")}&output=embed`;

  const openGate = (cfg) => setGate(cfg);

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Residence", name: p.name,
    description: p.overview, address: { "@type": "PostalAddress", addressLocality: p.location, addressRegion: "Karnataka", addressCountry: "IN" },
  };

  return (
    <div className="pb-24 lg:pb-0">
      <SEO title={`${p.name} by ${p.builder}, ${p.location} | Mathura Homes`} description={p.overview} image={images[0]} jsonLd={jsonLd} />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-gold">Home</Link> / <Link to="/properties" className="hover:text-gold">Properties</Link> / <span className="text-navy">{p.name}</span>
      </div>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="relative rounded-2xl overflow-hidden aspect-[16/9] md:aspect-[21/9] bg-navy">
          <img src={images[active]} alt={`${p.name} - view ${active + 1}`} className="w-full h-full object-cover" />
          {images.length > 1 && (
            <>
              <button onClick={() => setActive((active - 1 + images.length) % images.length)} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow" data-testid="gallery-prev"><ChevronLeft className="w-5 h-5 text-navy" /></button>
              <button onClick={() => setActive((active + 1) % images.length)} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow" data-testid="gallery-next"><ChevronRight className="w-5 h-5 text-navy" /></button>
            </>
          )}
        </div>
        {images.length > 1 && (
          <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
            {images.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} className={`shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 ${i === active ? "border-gold" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                <img src={img} alt={`thumbnail ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-3 gap-10">
        {/* Left content */}
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {p.new_launch && <Badge>New Launch</Badge>}
              {p.trending && <Badge className="bg-navy text-white">Trending</Badge>}
              {p.rera_approved && <Badge className="bg-emerald-50 text-emerald-700">RERA Approved</Badge>}
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-navy">{p.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-muted-foreground">
              <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-gold" /> {p.builder}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" /> {p.location}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-gold" /> {p.possession}</span>
            </div>
            <div className="mt-5 p-5 rounded-2xl bg-muted flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Price Range</p>
                <p className="font-display text-3xl text-navy font-semibold">{p.starting_price_label}{p.price_max ? ` – ₹${(p.price_max / 10000000).toFixed(2)} Cr` : "+"}</p>
              </div>
              <Button onClick={() => openGate({ leadType: "price", title: `Get Pricing for ${p.name}` })} className="bg-gold text-navy hover:bg-gold-dark font-semibold" data-testid="detail-get-price">Get Detailed Price</Button>
            </div>
          </div>

          <Info title="Configurations & Unit Sizes">
            <div className="flex flex-wrap gap-2">
              {(p.configurations || []).map((c) => <span key={c} className="px-3 py-1.5 rounded-lg bg-navy/5 text-navy text-sm font-medium">{c}</span>)}
            </div>
            <p className="text-sm text-muted-foreground mt-3">Unit sizes: {p.unit_sizes}</p>
          </Info>

          <Info title="Project Overview"><p className="text-muted-foreground leading-relaxed">{p.overview}</p></Info>

          {p.highlights?.length > 0 && (
            <Info title="Key Highlights">
              <div className="grid sm:grid-cols-2 gap-3">
                {p.highlights.map((h) => <div key={h} className="flex items-center gap-2 text-navy"><CheckCircle2 className="w-5 h-5 text-emerald-brand shrink-0" /> <span className="text-sm">{h}</span></div>)}
              </div>
            </Info>
          )}

          {p.amenities?.length > 0 && (
            <Info title="Amenities">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {p.amenities.map((a) => <div key={a} className="flex items-center gap-2 text-sm text-navy p-3 rounded-lg bg-muted"><CheckCircle2 className="w-4 h-4 text-gold shrink-0" /> {a}</div>)}
              </div>
            </Info>
          )}

          {/* Plans (gated floor plans) */}
          <Info title="Master Plan & Floor Plans">
            <Tabs defaultValue="master">
              <TabsList>
                <TabsTrigger value="master" data-testid="tab-master">Master Plan</TabsTrigger>
                <TabsTrigger value="floor" data-testid="tab-floor">Floor Plans</TabsTrigger>
              </TabsList>
              <TabsContent value="master" className="mt-4">
                {p.master_plan ? <img src={p.master_plan} alt="Master plan" className="rounded-xl w-full max-h-[420px] object-contain bg-muted" /> : <Empty text="Master plan available on request." />}
              </TabsContent>
              <TabsContent value="floor" className="mt-4">
                {(p.floor_plans || []).length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {p.floor_plans.map((fp, i) => (
                      <div key={i} className="relative rounded-xl overflow-hidden border border-gray-100">
                        <img src={fp.image} alt={`Floor plan ${fp.config}`} className="w-full h-56 object-cover blur-md" />
                        <div className="absolute inset-0 bg-navy/60 flex flex-col items-center justify-center text-white p-4">
                          <Layers className="w-8 h-8 text-gold mb-2" />
                          <p className="font-display text-lg">{fp.config} · {fp.size}</p>
                          <Button onClick={() => openGate({ leadType: "floor_plan", title: `View ${fp.config} Floor Plan` })} className="mt-3 bg-gold text-navy hover:bg-gold-dark" size="sm" data-testid={`view-floor-plan-${i}`}>View Floor Plan</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <Empty text="Floor plans available on request." />}
              </TabsContent>
            </Tabs>
          </Info>

          {/* Price table */}
          {p.price_table?.length > 0 && (
            <Info title="Price Table">
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-navy text-white"><tr><th className="text-left p-3">Configuration</th><th className="text-left p-3">Size</th><th className="text-left p-3">Price</th></tr></thead>
                  <tbody>
                    {p.price_table.map((r, i) => <tr key={i} className={i % 2 ? "bg-muted" : "bg-white"}><td className="p-3 font-medium text-navy">{r.config}</td><td className="p-3 text-muted-foreground">{r.size}</td><td className="p-3 text-navy font-semibold">{r.price}</td></tr>)}
                  </tbody>
                </table>
              </div>
              {p.payment_plan && <p className="text-sm text-muted-foreground mt-4"><strong className="text-navy">Payment Plan:</strong> {p.payment_plan}</p>}
            </Info>
          )}

          {/* Nearby */}
          {p.nearby && (
            <Info title="Location & Connectivity">
              <div className="rounded-xl overflow-hidden border border-gray-100 mb-5">
                <iframe title="map" src={mapSrc} className="w-full h-72 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Nearby icon={School} title="Schools" items={p.nearby.schools} />
                <Nearby icon={Hospital} title="Hospitals" items={p.nearby.hospitals} />
                <Nearby icon={Briefcase} title="Offices & IT Hubs" items={p.nearby.offices} />
                <Nearby icon={Train} title="Transport" items={p.nearby.transport} />
              </div>
            </Info>
          )}

          {/* RERA */}
          <Info title="RERA Information">
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-700 shrink-0" />
              <div><p className="font-semibold text-navy">RERA Number</p><p className="text-sm text-muted-foreground">{p.rera || "Available on request"}</p>
              <p className="text-xs text-muted-foreground mt-2">Please verify RERA details on the official Karnataka RERA portal before booking.</p></div>
            </div>
          </Info>

          {/* Brochure */}
          <div className="p-6 rounded-2xl bg-navy text-white flex flex-col sm:flex-row items-center justify-between gap-4">
            <div><h3 className="font-display text-xl">Download Project Brochure</h3><p className="text-sm text-gray-300">Get complete details, specifications and pricing.</p></div>
            <Button onClick={() => openGate({ leadType: "brochure", title: `Download ${p.name} Brochure`, onUnlock: () => { if (p.brochure_url) window.open(p.brochure_url, "_blank"); } })} className="bg-gold text-navy hover:bg-gold-dark font-semibold" data-testid="download-brochure"><Download className="w-4 h-4 mr-2" /> Download Brochure</Button>
          </div>
        </div>

        {/* Right sticky forms */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
              <Tabs defaultValue="callback">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="callback" data-testid="tab-callback">Callback</TabsTrigger>
                  <TabsTrigger value="visit" data-testid="tab-visit">Site Visit</TabsTrigger>
                </TabsList>
                <TabsContent value="callback" className="mt-4">
                  <h3 className="font-display text-lg text-navy mb-3">Request a Callback</h3>
                  <LeadForm variant="gated" leadType="callback" projectName={p.name} sourcePage={`property:${p.slug}`} redirectThankYou={false} />
                </TabsContent>
                <TabsContent value="visit" className="mt-4">
                  <h3 className="font-display text-lg text-navy mb-3">Book a Site Visit</h3>
                  <LeadForm variant="gated" leadType="site_visit" projectName={p.name} sourcePage={`property:${p.slug}`} redirectThankYou={false} />
                </TabsContent>
              </Tabs>
            </div>
            <div className="hidden lg:flex gap-3">
              <a href={telLink()} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg border border-navy text-navy font-semibold hover:bg-navy hover:text-white transition-colors"><Phone className="w-4 h-4" /> Call</a>
              <a href={waLink(waMsg)} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-whatsapp text-white font-semibold"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>
        </aside>
      </div>

      {/* Similar */}
      {p.similar?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl md:text-3xl text-navy mb-8">Similar Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{p.similar.map((s) => <PropertyCard key={s.id} p={s} />)}</div>
        </section>
      )}

      {/* Sticky mobile CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-100 p-3 grid grid-cols-3 gap-2" data-testid="mobile-cta-bar">
        <a href={telLink()} className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg border border-navy text-navy text-xs font-semibold"><Phone className="w-4 h-4" /> Call</a>
        <a href={waLink(waMsg)} target="_blank" rel="noreferrer" className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg bg-whatsapp text-white text-xs font-semibold"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        <button onClick={() => openGate({ leadType: "callback", title: `Enquire about ${p.name}` })} className="inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg bg-gold text-navy text-xs font-semibold" data-testid="mobile-enquire"><HomeIcon className="w-4 h-4" /> Enquire</button>
      </div>

      {gate && <GatedLeadModal open={!!gate} onOpenChange={(o) => !o && setGate(null)} projectName={p.name} leadType={gate.leadType} title={gate.title} onUnlock={gate.onUnlock} description="Share your details and our advisor will assist you." />}
    </div>
  );
}

const Badge = ({ children, className = "bg-gold text-navy" }) => <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${className}`}>{children}</span>;
const Info = ({ title, children }) => <div><h2 className="font-display text-2xl text-navy mb-4">{title}</h2>{children}</div>;
const Empty = ({ text }) => <p className="text-muted-foreground text-sm py-6 text-center bg-muted rounded-xl">{text}</p>;
const Nearby = ({ icon: Icon, title, items }) => (
  <div className="p-4 rounded-xl border border-gray-100">
    <p className="flex items-center gap-2 font-semibold text-navy mb-2"><Icon className="w-4 h-4 text-gold" /> {title}</p>
    <ul className="text-sm text-muted-foreground space-y-1">{(items || []).map((it) => <li key={it}>• {it}</li>)}</ul>
  </div>
);
